import { ACTION_NAMES } from '@/constants/actionNames';
import { cacheImage, getImageFromCache, openDB } from '../utils/indexdb';
import Repository from './repository';

const resource = '/cases';
const fullDayInSeconds = 60 * 60 * 24;

type CasePayload = Record<string, unknown>;
type CaseHeaders = Record<string, unknown>;

type GetInitialDataArgs = {
	caseId: string;
	headers?: CaseHeaders;
};

type CreateCaseTypeArgs = {
	merchantId: string;
	payload: CasePayload;
};

type FindCaseTypesArgs = {
	merchantId: string;
	payload: CasePayload;
	headers?: CaseHeaders;
};

type UpdateCaseTypeArgs = {
	merchantId: string;
	id: string;
	payload: CasePayload;
};

type DeleteCaseTypeArgs = {
	payload: CasePayload;
	headers?: CaseHeaders;
};

type CreateStatusArgs = {
	payload: CasePayload;
};

type FindStatusesArgs = {
	payload: CasePayload;
	headers?: CaseHeaders;
};

type FindCaseStatusSetsArgs = {
	merchantId?: string;
	headers?: CaseHeaders;
};

type FindCaseStatusSetByIdArgs = {
	id: string;
	headers?: CaseHeaders;
};

type CreateCaseStatusSetArgs = {
	payload: CasePayload;
	headers?: CaseHeaders;
};

type UpdateCaseStatusSetArgs = {
	id: string;
	payload: CasePayload;
	headers?: CaseHeaders;
};

type DeleteCaseStatusSetArgs = {
	id: string;
	payload?: CasePayload;
	headers?: CaseHeaders;
};

type UpdateStatusArgs = {
	id: string;
	payload: CasePayload;
	headers?: CaseHeaders;
};

type DeleteStatusArgs = {
	payload: CasePayload;
	headers?: CaseHeaders;
};

type FindCustomFieldSetsArgs = {
	merchantId?: string;
	headers?: CaseHeaders;
};

type FindCasesUsingResourceArgs = {
	resourceType: 'casetypes' | 'statuses' | 'statussets' | 'customfieldset';
	id: string;
	headers?: CaseHeaders;
};

type CreateCustomFieldSetArgs = {
	payload: CasePayload;
	headers?: CaseHeaders;
};

type UpdateCustomFieldSetArgs = {
	id: string;
	payload: CasePayload;
	headers?: CaseHeaders;
};

type DeleteCustomFieldSetArgs = {
	id: string;
	headers?: CaseHeaders;
};

type SetDefaultStatusArgs = {
	id: string;
	payload: CasePayload;
};

type GetCaseConstantsArgs = {
	merchantId?: string;
	caseId?: string;
	payload: CasePayload;
	headers?: CaseHeaders;
};

type FindArgs = {
	id: string;
	payload: CasePayload;
	headers?: CaseHeaders;
};

type GetAmlLinkedToEntityArgs = {
	complyId: string;
};

type CreateCaseArgs = {
	payload: CasePayload;
	headers?: CaseHeaders;
};

type SubmitProofOfAddressArgs = {
	payload: FormData;
	headers?: CaseHeaders;
};

type FindProofOfAddressSubmissionsArgs = {
	payload: CasePayload;
	headers?: CaseHeaders;
};

type ProofOfAddressSubmissionIdArgs = {
	id: string;
	headers?: CaseHeaders;
};

type LinkProofOfAddressToEntityArgs = {
	caseId: string;
	entityId: string;
	submissionId: string;
	payload?: CasePayload;
	headers?: CaseHeaders;
};

type DeleteProofOfAddressSubmissionsArgs = {
	payload: CasePayload;
	headers?: CaseHeaders;
};

type GetEntityListArgs = {
	id?: string;
	payload?: CasePayload;
};

type GetEntitiesByCaseArgs = {
	merchantId?: string | null;
	caseId: string;
	payload: CasePayload;
};

type GetDocumentTypesArgs = {
	payload: CasePayload;
};

type UploadCaseDocArgs = {
	merchantId: string;
	payload: CasePayload;
};

type LinkDocToEntityArgs = {
	caseId: string;
	payload: CasePayload;
};

type GetFilesByCaseIdArgs = {
	caseId: string;
	payload: CasePayload;
};

type GetDocumentByIdArgs = {
	merchantId: string;
	docId?: string;
};

type GetConfigsArgs = {
	payload: CasePayload;
};

type DeleteDocumentArgs = {
	caseId: string;
	payload: CasePayload;
};

type DeleteCasesArgs = {
	payload: CasePayload;
	headers?: CaseHeaders;
};

type CreateNestedResourceArgs = {
	id: string;
	payload: CasePayload;
};

type GetCaseDocumentsArgs = {
	caseId: string;
};

type GroupedDocumentsMutationArgs = {
	payload: CasePayload;
	headers?: CaseHeaders;
};

type GetAutomationDataImagesStreamingArgs = {
	groupedDocumentId: string;
	images: Array<{
		base64Images: Array<{
			base64File: string;
			[key: string]: unknown;
		}>;
		[key: string]: unknown;
	}>;
};

type GetAutomationDataImagesArgs = {
	groupedDocumentId: string;
};

type CaseIdArgs = {
	caseId: string;
};

type SessionIdArgs = {
	sessionId: string;
};

type DownloadCSVCasesArgs = {
	payload: CasePayload;
};

const stripMerchantFields = (payload: CasePayload = {}): CasePayload => {
	const { merchantId, merchantid, ...rest } = payload as CasePayload & {
		merchantId?: unknown;
		merchantid?: unknown;
	};

	void merchantId;
	void merchantid;

	return rest;
};

export default {
	find({ id, payload, headers = {} }: FindArgs) {
		return Repository.postWithAuth(`${resource}/cases/find`, payload, {
			'x-action-name': ACTION_NAMES.LOAD_ALL_CASES,
			...headers,
		});
	},
	getAmlLinkedToEntity({ complyId }: GetAmlLinkedToEntityArgs) {
		return Repository.getWithAuth(
			`${resource}/cases/findLinkedEntity/${complyId}`,
			{
				'x-action-name': ACTION_NAMES.CHECK_AML_LINK_WITH_ENTITY,
				payload: {},
			}
		);
	},
	linkAMLSearchToEntity({
		caseId,
		entityId,
		complyId,
		payload,
	}: {
		caseId: string;
		entityId: string;
		complyId: string;
		payload: CasePayload;
	}) {
		return Repository.postWithAuth(
			`${resource}/cases/${caseId}/${entityId}/${complyId}/link`,
			{
				'x-action-name': ACTION_NAMES.LINK_AML_SEARCH_TO_ENTITY,
				payload,
			}
		);
	},
	unLinkAMLSearchToEntity({
		caseId,
		entityId,
		complyId,
		payload,
	}: {
		caseId: string;
		entityId: string;
		complyId: string;
		payload: CasePayload;
	}) {
		return Repository.postWithAuth(
			`${resource}/cases/${caseId}/${entityId}/${complyId}/unlink`,
			{
				'x-action-name': ACTION_NAMES.UNLINK_AML_SEARCH_TO_ENTITY,
				payload,
			}
		);
	},
	linkFormSubmissionToEntity({
		caseId,
		entityId,
		submissionId,
	}: {
		caseId: string;
		entityId: string;
		submissionId: string;
	}) {
		return Repository.postWithAuth(
			`${resource}/cases/${caseId}/${entityId}/form-submissions/${submissionId}/link`,
			{}
		);
	},
	unlinkFormSubmissionFromEntity({
		caseId,
		entityId,
		submissionId,
	}: {
		caseId: string;
		entityId: string;
		submissionId: string;
	}) {
		return Repository.postWithAuth(
			`${resource}/cases/${caseId}/${entityId}/form-submissions/${submissionId}/unlink`,
			{}
		);
	},
	linkPoaToEntity({
		caseId,
		entityId,
		submissionId,
		payload = {},
		headers = {},
	}: LinkProofOfAddressToEntityArgs) {
		return Repository.postWithAuth(
			`${resource}/cases/${caseId}/${entityId}/proof-of-address/${submissionId}/link`,
			payload,
			{
				'x-action-name':
					ACTION_NAMES.LINK_PROOF_OF_ADDRESS_SUBMISSION_TO_ENTITY,
				...headers,
			}
		);
	},
	unlinkPoaFromEntity({
		caseId,
		entityId,
		submissionId,
		payload = {},
		headers = {},
	}: LinkProofOfAddressToEntityArgs) {
		return Repository.postWithAuth(
			`${resource}/cases/${caseId}/${entityId}/proof-of-address/${submissionId}/unlink`,
			payload,
			{
				'x-action-name':
					ACTION_NAMES.UNLINK_PROOF_OF_ADDRESS_SUBMISSION_FROM_ENTITY,
				...headers,
			}
		);
	},
	/**
	 * @param {{ caseId: string, headers?: CaseHeaders }} args
	 */
	getInitialData({ caseId, headers = {} }: GetInitialDataArgs) {
		return Repository.getWithAuth(`${resource}/data/${caseId}`, {
			'x-action-name': ACTION_NAMES.LOAD_ALL_CASES,
			payload: {},
			...headers,
		});
	},
	getEntityFiles({
		payload,
		caseId,
		entityLinkId,
		headers = {},
	}: {
		payload: CasePayload;
		caseId: string;
		entityLinkId: string;
		headers?: CaseHeaders;
	}) {
		return Repository.postWithAuth(
			`${resource}/documents/${caseId}/${entityLinkId}/find`,
			payload,
			headers,
			{ noCache: true }
		);
	},
	loadCaseRevisions({
		id,
		payload,
		headers = {},
	}: {
		id: string;
		payload: CasePayload;
		headers?: CaseHeaders;
	}) {
		return Repository.postWithAuth(
			`${resource}/cases/${id}/revisions/find`,
			payload,
			{ 'x-action-name': ACTION_NAMES.REVISIONS_OF_CASE, ...headers }
		);
	},
	loadPersonRevisions({
		caseId,
		id,
		payload,
	}: {
		caseId: string;
		id: string;
		payload: CasePayload;
	}) {
		return Repository.postWithAuth(
			`${resource}/persons/${caseId}/${id}/revisions/find`,
			payload,
			{ 'x-action-name': ACTION_NAMES.REVISIONS_OF_PERSON_DOCUMENT }
		);
	},
	loadCompanyRevisions({
		caseId,
		id,
		payload,
	}: {
		caseId: string;
		id: string;
		payload: CasePayload;
	}) {
		return Repository.postWithAuth(
			`${resource}/companies/${caseId}/${id}/revisions/find`,
			payload,
			{ 'x-action-name': ACTION_NAMES.REVISIONS_OF_COMPANY_DOCUMENT }
		);
	},
	createCase({ payload, headers = {} }: CreateCaseArgs) {
		return Repository.postWithAuth(`${resource}/cases`, payload, headers, {
			noCache: true,
		});
	},
	submitProofOfAddress({ payload, headers = {} }: SubmitProofOfAddressArgs) {
		return Repository.postWithAuth(`${resource}/poa`, payload, {
			'x-action-name': ACTION_NAMES.ANALYZE_PROOF_OF_ADDRESS,
			'Content-Type': 'multipart/form-data',
			...headers,
		});
	},
	findProofOfAddressSubmissions({
		payload,
		headers = {},
	}: FindProofOfAddressSubmissionsArgs) {
		return Repository.postWithAuth(`${resource}/poa/find`, payload, headers, {
			noCache: true,
		});
	},
	getProofOfAddressSubmissionById({
		id,
		headers = {},
	}: ProofOfAddressSubmissionIdArgs) {
		return Repository.getWithAuth(`${resource}/poa/find/${id}`, {}, headers, {
			noCache: true,
		});
	},
	downloadProofOfAddressSubmission({
		id,
		headers = {},
	}: ProofOfAddressSubmissionIdArgs) {
		return Repository.getWithAuth(
			`${resource}/poa/download/${id}`,
			{},
			headers,
			{
				noCache: true,
				responseType: 'blob',
			}
		);
	},
	async deleteProofOfAddressSubmissions({
		payload,
		headers = {},
	}: DeleteProofOfAddressSubmissionsArgs) {
		const requestHeaders = {
			'x-action-name': ACTION_NAMES.DELETE_PROOF_OF_ADDRESS_SUBMISSIONS,
			...headers,
		};
		// Blank strings are filtered out too, not just non-strings: an empty id would otherwise
		// build `${resource}/poa/` and fire a request against the collection route.
		const submissionIds = Array.isArray(payload?.ids)
			? payload.ids.filter(
					(value): value is string =>
						typeof value === 'string' && value.trim() !== ''
				)
			: [];

		if (!submissionIds.length) {
			return {
				data: {
					success: false,
					errors: [{ message: 'No proof of address submission ids provided' }],
				},
			};
		}

		if (submissionIds.length === 1) {
			return Repository.deleteWithAuth(
				`${resource}/poa/${submissionIds[0]}`,
				{},
				requestHeaders
			);
		}

		// Deliberately allSettled rather than all: the delete endpoint rejects per submission (409
		// for a linked or CFM-captured one), and every request has already fired by the time the
		// first rejection lands. Fail-fasting would report a blanket failure while the eligible
		// rows had in fact been deleted, leaving the caller unable to tell which ones survived.
		const results = await Promise.allSettled(
			submissionIds.map((submissionId) =>
				Repository.deleteWithAuth(
					`${resource}/poa/${submissionId}`,
					{},
					requestHeaders
				)
			)
		);

		const deletedIds = submissionIds.filter(
			(_id, index) => results[index]?.status === 'fulfilled'
		);
		const errors = results
			.map((result, index) =>
				result.status === 'rejected'
					? {
							id: submissionIds[index],
							message:
								result.reason?.response?.data?.errors?.[0]?.message ||
								result.reason?.response?.data?.message ||
								result.reason?.message ||
								'Failed to delete proof of address submission',
						}
					: null
			)
			.filter(
				(error): error is { id: string; message: string } => error !== null
			);

		if (errors.length) {
			return {
				data: {
					success: false,
					data: {
						ids: deletedIds,
					},
					errors,
				},
			};
		}

		return {
			data: {
				success: true,
				data: {
					ids: deletedIds,
				},
			},
		};
	},
	getCaseDetailsByID({
		caseId,
		payload,
		headers = {},
	}: {
		caseId: string;
		payload: CasePayload;
		headers?: CaseHeaders;
	}) {
		return Repository.getWithAuth(
			`${resource}/cases/${caseId}`,
			payload,
			headers
		);
	},
	getEntitiesByMerchantId({ id, payload }: GetEntityListArgs) {
		void id;
		void payload;
		return Repository.getWithAuthAndCache(
			`${resource}/entities/find`,
			fullDayInSeconds
		);
	},
	createEntity({
		id,
		type,
		payload,
		headers = {},
	}: {
		id: string;
		type: string;
		payload: CasePayload;
		headers?: CaseHeaders;
	}) {
		return Repository.postWithAuth(
			`${resource}/${type}/${id}`,
			payload,
			headers,
			{ noCache: true }
		);
	},
	retryCertificate({
		caseId,
		entityId,
		payload,
		headers = {},
	}: {
		caseId: string;
		entityId: string;
		payload: CasePayload;
		headers?: CaseHeaders;
	}) {
		return Repository.postWithAuth(
			`${resource}/cases/${caseId}/${entityId}/retry/certificate`,
			payload,
			headers,
			{ noCache: true }
		);
	},
	updateCase({
		caseId,
		payload,
		headers = {},
	}: {
		caseId: string;
		payload: CasePayload;
		headers?: CaseHeaders;
	}) {
		return Repository.putWithAuth(
			`${resource}/cases/${caseId}`,
			payload,
			headers,
			{ noCache: true }
		);
	},
	getPersons({ id, payload }: CreateNestedResourceArgs) {
		return Repository.postWithAuth(`${resource}/persons/${id}/find`, payload);
	},
	getCompanies({ id, payload }: CreateNestedResourceArgs) {
		return Repository.postWithAuth(`${resource}/companies/${id}/find`, payload);
	},
	getCaseEntitiesWithCaseID({
		merchantId,
		caseId,
		payload,
	}: GetEntitiesByCaseArgs) {
		void merchantId;
		void payload;

		return Repository.getWithAuthAndCache(
			`${resource}/cases/${caseId}/entities/linked`,
			1
		);
	},
	getEntityResults({ caseId, entityId }: { caseId: string; entityId: string }) {
		return Repository.getWithAuth(
			`${resource}/cases/${caseId}/entities/${entityId}/results`,
			undefined,
			undefined,
			{ noCache: true }
		);
	},
	updateCaseStatus({
		caseId,
		payload,
		headers = {},
	}: {
		caseId: string;
		payload: CasePayload;
		headers?: CaseHeaders;
	}) {
		return Repository.putWithAuth(
			`${resource}/cases/${caseId}/status`,
			payload,
			headers,
			{ noCache: true }
		);
	},
	getDocumentTypesByMerchantId({ payload }: GetDocumentTypesArgs) {
		return Repository.postWithAuth(`${resource}/documenttypes/find`, payload);
	},
	updateEntity({
		caseId,
		type,
		payload,
		headers = {},
	}: {
		caseId: string;
		type: string;
		payload: CasePayload;
		headers?: CaseHeaders;
	}) {
		return Repository.putWithAuth(
			`${resource}/${type}/${caseId}/${payload.id}`,
			payload,
			headers,
			{ noCache: true }
		);
	},
	getDocumentSide({ payload }: GetDocumentTypesArgs) {
		return Repository.postWithAuth(`${resource}/utilities/configs`, payload);
	},
	uploadCaseDoc({ merchantId, payload }: UploadCaseDocArgs) {
		return Repository.postWithAuthUploadCaseDoc(
			`${resource}/documents/${merchantId}`,
			payload,
			{},
			{ noCache: true }
		);
	},
	uploadBinaryCaseDoc({
		caseId,
		payload,
		headers = {},
	}: {
		caseId: string;
		payload: CasePayload;
		headers?: CaseHeaders;
	}) {
		return Repository.postWithAuthCaseBase64Doc(
			`${resource}/documents/upload/${caseId}`,
			payload,
			headers,
			{ noCache: true }
		);
	},
	linkDocToEntity({ caseId, payload }: LinkDocToEntityArgs) {
		return Repository.postWithAuth(
			`${resource}/documents/${caseId}/entities/link`,
			payload,
			{},
			{ noCache: true }
		);
	},
	unlinkDocFromEntity({ caseId, payload }: LinkDocToEntityArgs) {
		return Repository.postWithAuth(
			`${resource}/documents/${caseId}/entities/unlink`,
			payload,
			{},
			{ noCache: true }
		);
	},
	getFilesByCaseId({ caseId, payload }: GetFilesByCaseIdArgs) {
		return Repository.postWithAuth(
			`${resource}/documents/${caseId}/find`,
			payload
		);
	},
	getDocumentById({ merchantId, docId }: GetDocumentByIdArgs) {
		void docId;
		return Repository.getWithAuth(`${resource}/documents/${merchantId}`);
	},
	getConfigs({ payload }: GetConfigsArgs) {
		return Repository.postWithAuth(`${resource}/utilities/configs`, payload);
	},
	deleteDocument({ caseId, payload }: DeleteDocumentArgs) {
		return Repository.deleteWithAuth(
			`${resource}/documents//${caseId}`,
			payload
		);
	},
	async deleteCases({ payload, headers = {} }: DeleteCasesArgs) {
		const requestHeaders = {
			'x-action-name': ACTION_NAMES.DELETE_CASES,
			...headers,
		};
		const caseIds = Array.isArray(payload?.ids)
			? payload.ids.filter(
					(value): value is string => typeof value === 'string'
				)
			: [];

		if (!caseIds.length) {
			return {
				data: {
					success: false,
					errors: [{ message: 'No case ids provided' }],
				},
			};
		}

		if (caseIds.length === 1) {
			return Repository.deleteWithAuth(
				`${resource}/cases/${caseIds[0]}`,
				{},
				requestHeaders,
				{ noCache: true }
			);
		}

		const responses = await Promise.all(
			caseIds.map((caseId) =>
				Repository.deleteWithAuth(
					`${resource}/cases/${caseId}`,
					{},
					requestHeaders,
					{ noCache: true }
				)
			)
		);

		const failedResponse = responses.find(
			(response) => response?.data?.success === false
		);
		if (failedResponse) {
			return failedResponse;
		}

		return {
			data: {
				success: true,
				data: {
					ids: caseIds,
				},
			},
		};
	},
	deleteDocuments({
		merchantId,
		payload,
		headers = {},
	}: {
		merchantId: string;
		payload: CasePayload;
		headers?: CaseHeaders;
	}) {
		return Repository.deleteWithAuth(
			`${resource}/documents/${merchantId}`,
			payload,
			headers,
			{ noCache: true }
		);
	},
	findCaseStatusesWithCaseID({
		merchantId,
		caseId,
		payload,
	}: {
		merchantId?: string | null;
		caseId: string;
		payload: CasePayload;
	}) {
		void merchantId;
		return Repository.postWithAuth(
			`${resource}/cases/${caseId}/find/statuses`,
			payload
		);
	},
	updateFileDetails({
		caseId,
		docId,
		payload,
	}: {
		caseId: string;
		docId: string;
		payload: CasePayload;
	}) {
		return Repository.putWithAuth(
			`${resource}/documents/${caseId}/${docId}/organize`,
			payload,
			{ 'x-action-name': ACTION_NAMES.UPDATE_FILE_DETAILS },
			{ noCache: true }
		);
	},
	downloadAsZip({
		caseId,
		payload,
		headers = {},
	}: {
		caseId: string;
		payload: CasePayload;
		headers?: CaseHeaders;
	}) {
		return Repository.postWithAuthForZipFromResponse(
			`${resource}/documents/${caseId}/zip`,
			payload,
			{ 'x-action-name': ACTION_NAMES.DOWNLOAD_ENTITY_FILE, ...headers }
		);
	},
	/**
	 * Case type lookup is resolved by the backend for the active merchant context.
	 */
	async findCaseTypes({
		merchantId,
		payload,
		headers = {},
	}: FindCaseTypesArgs) {
		void merchantId;

		return Repository.postWithAuth(
			`${resource}/casetypes/find`,
			stripMerchantFields(payload),
			{ 'x-action-name': ACTION_NAMES.FIND_CASE_TYPE, ...headers }
		);
	},
	/**
	 * @param {{ payload: CasePayload }} args
	 */
	createNewCaseType({ merchantId, payload }: CreateCaseTypeArgs) {
		void merchantId;
		return Repository.postWithAuth(
			`${resource}/casetypes`,
			stripMerchantFields(payload),
			{ 'x-action-name': ACTION_NAMES.CREATE_CASE_TYPE },
			{ noCache: true }
		);
	},
	/**
	 * @param {{ id: string, payload: CasePayload }} args
	 */
	updateCaseType({ merchantId, id, payload }: UpdateCaseTypeArgs) {
		void merchantId;
		return Repository.putWithAuth(
			`${resource}/casetypes/${id}`,
			stripMerchantFields(payload),
			{ 'x-action-name': ACTION_NAMES.UPDATE_CASE_TYPE },
			{ noCache: true }
		);
	},
	/**
	 * @param {{ payload: CasePayload, headers?: CaseHeaders }} args
	 */
	deleteCaseType({ payload, headers = {} }: DeleteCaseTypeArgs) {
		return Repository.deleteWithAuth(
			`${resource}/casetypes`,
			stripMerchantFields(payload),
			{ 'x-action-name': ACTION_NAMES.DELETE_CASE_TYPE, ...headers },
			{ noCache: true }
		);
	},
	findCustomFieldSets({ merchantId, headers = {} }: FindCustomFieldSetsArgs) {
		void merchantId;
		return Repository.getWithAuth(
			`${resource}/customfieldset/find`,
			{},
			{ 'x-action-name': ACTION_NAMES.FIND_CUSTOM_FIELD_SETS, ...headers }
		);
	},
	findCasesUsingResource({
		resourceType,
		id,
		headers = {},
	}: FindCasesUsingResourceArgs) {
		return Repository.getWithAuth(`${resource}/${resourceType}/${id}/cases`, {
			'x-action-name': ACTION_NAMES.FIND_CASES_USING_RESOURCE,
			payload: {},
			...headers,
		});
	},
	createCustomFieldSet({ payload, headers = {} }: CreateCustomFieldSetArgs) {
		return Repository.postWithAuth(
			`${resource}/customfieldset`,
			stripMerchantFields(payload),
			{ 'x-action-name': ACTION_NAMES.CREATE_CUSTOM_FIELD_SET, ...headers },
			{ noCache: true }
		);
	},
	updateCustomFieldSet({
		id,
		payload,
		headers = {},
	}: UpdateCustomFieldSetArgs) {
		return Repository.postWithAuth(
			`${resource}/customfieldset/${id}`,
			stripMerchantFields(payload),
			{ 'x-action-name': ACTION_NAMES.UPDATE_CUSTOM_FIELD_SET, ...headers },
			{ noCache: true }
		);
	},
	deleteCustomFieldSet({ id, headers = {} }: DeleteCustomFieldSetArgs) {
		return Repository.deleteWithAuth(
			`${resource}/customfieldset/${id}`,
			{},
			{ 'x-action-name': ACTION_NAMES.DELETE_CUSTOM_FIELD_SET, ...headers },
			{ noCache: true }
		);
	},
	/**
	 * @param {{ payload: CasePayload }} args
	 */
	createNewStatus({ payload }: CreateStatusArgs) {
		return Repository.postWithAuth(
			`${resource}/statuses`,
			stripMerchantFields(payload),
			{ 'x-action-name': ACTION_NAMES.CREATE_CASE_STATUS },
			{ noCache: true }
		);
	},
	findStatuses({ payload, headers = {} }: FindStatusesArgs) {
		return Repository.postWithAuth(
			`${resource}/statuses/find`,
			stripMerchantFields(payload),
			{ 'x-action-name': ACTION_NAMES.FIND_CASE_STATUS, ...headers }
		);
	},
	findCaseStatusSets({ merchantId, headers = {} }: FindCaseStatusSetsArgs) {
		void merchantId;

		return Repository.getWithAuth(
			`${resource}/casestatusset/find`,
			{},
			{ 'x-action-name': ACTION_NAMES.FIND_CASE_STATUS_SETS, ...headers }
		);
	},
	findCaseStatusSetById({ id, headers = {} }: FindCaseStatusSetByIdArgs) {
		return Repository.getWithAuth(
			`${resource}/casestatusset/find/${id}`,
			{},
			{ 'x-action-name': ACTION_NAMES.FIND_CASE_STATUS_SET, ...headers }
		);
	},
	createCaseStatusSet({ payload, headers = {} }: CreateCaseStatusSetArgs) {
		return Repository.postWithAuth(
			`${resource}/casestatusset`,
			stripMerchantFields(payload),
			{ 'x-action-name': ACTION_NAMES.CREATE_CASE_STATUS_SET, ...headers },
			{ noCache: true }
		);
	},
	updateCaseStatusSet({ id, payload, headers = {} }: UpdateCaseStatusSetArgs) {
		return Repository.postWithAuth(
			`${resource}/casestatusset/${id}`,
			stripMerchantFields(payload),
			{ 'x-action-name': ACTION_NAMES.UPDATE_CASE_STATUS_SET, ...headers },
			{ noCache: true }
		);
	},
	deleteCaseStatusSet({
		id,
		payload = {},
		headers = {},
	}: DeleteCaseStatusSetArgs) {
		return Repository.deleteWithAuth(
			`${resource}/casestatusset/${id}`,
			stripMerchantFields(payload),
			{ 'x-action-name': ACTION_NAMES.DELETE_CASE_STATUS_SET, ...headers },
			{ noCache: true }
		);
	},
	findCaseStatuses({ payload, headers = {} }: DeleteStatusArgs) {
		return Repository.postWithAuth(
			`${resource}/casestatuses/`,
			stripMerchantFields(payload),
			{ 'x-action-name': ACTION_NAMES.FIND_CASE_STATUSES, ...headers },
			{ noCache: true }
		);
	},
	/**
	 * @param {{ id: string, payload: CasePayload, headers?: CaseHeaders }} args
	 */
	updateStatus({ id, payload, headers = {} }: UpdateStatusArgs) {
		return Repository.putWithAuth(
			`${resource}/statuses/${id}`,
			stripMerchantFields(payload),
			{ 'x-action-name': ACTION_NAMES.UPDATE_CASE_STATUS_SETTING, ...headers },
			{ noCache: true }
		);
	},
	/**
	 * @param {{ payload: CasePayload, headers?: CaseHeaders }} args
	 */
	deleteStatus({ payload, headers = {} }: DeleteStatusArgs) {
		return Repository.deleteWithAuth(
			`${resource}/statuses`,
			stripMerchantFields(payload),
			{ 'x-action-name': ACTION_NAMES.DELETE_CASE_STATUS, ...headers },
			{ noCache: true }
		);
	},
	/**
	 * @param {{ id: string, payload: CasePayload }} args
	 */
	setDefaultStatus({ id, payload }: SetDefaultStatusArgs) {
		return Repository.putWithAuth(
			`${resource}/statuses/${id}/default`,
			stripMerchantFields(payload),
			{ 'x-action-name': ACTION_NAMES.SET_DEFAULT_CASE_STATUS },
			{ noCache: true }
		);
	},
	createCompanies({ id, payload }: CreateNestedResourceArgs) {
		return Repository.postWithAuth(`${resource}/companies/${id}`, payload);
	},
	updateCompanies({
		caseId,
		id,
		payload,
		headers = {},
	}: {
		caseId: string;
		id: string;
		payload: CasePayload;
		headers?: CaseHeaders;
	}) {
		return Repository.putWithAuth(
			`${resource}/companies/${caseId}/${id}`,
			payload,
			headers,
			{ noCache: true }
		);
	},
	createDocuments({ id, payload }: CreateNestedResourceArgs) {
		return Repository.postWithAuth(`${resource}/documents/${id}`, payload);
	},
	getImageDetailsFromCaseID({
		caseId,
		payload,
		headers = {},
	}: {
		caseId: string;
		payload: CasePayload;
		headers?: CaseHeaders;
	}) {
		return Repository.postWithAuth(
			`${resource}/documents/${caseId}/details`,
			payload,
			headers
		);
	},
	amlSearch({
		caseId,
		entityId,
		payload,
		headers = {},
	}: {
		caseId: string;
		entityId: string;
		payload: CasePayload;
		headers?: CaseHeaders;
	}) {
		return Repository.postWithAuth(
			`${resource}/cases/${caseId}/${entityId}/search`,
			payload,
			headers,
			{ noCache: true }
		);
	},
	retrySearch({
		caseId,
		entityId,
		payload,
		headers = {},
	}: {
		caseId: string;
		entityId: string;
		payload: CasePayload;
		headers?: CaseHeaders;
	}) {
		return Repository.postWithAuth(
			`${resource}/cases/${caseId}/${entityId}/retry`,
			payload,
			headers,
			{ noCache: true }
		);
	},

	getCaseDocuments({ caseId }: GetCaseDocumentsArgs) {
		return Repository.getWithAuth(`${resource}/documents/grouped/${caseId}`);
	},

	getAllDocuments({ payload, headers = {} }: GroupedDocumentsMutationArgs) {
		return Repository.postWithAuth(
			`${resource}/documents/grouped/data`,
			payload,
			headers
		);
	},
	postRotation({ payload, headers = {} }: GroupedDocumentsMutationArgs) {
		return Repository.postWithAuth(
			`${resource}/documents/grouped/document/rotation`,
			payload,
			headers,
			{ noCache: true }
		);
	},
	updateDocumentFields({
		payload,
		headers = {},
	}: GroupedDocumentsMutationArgs) {
		return Repository.putWithAuth(
			`${resource}/documents/grouped/metadata`,
			payload,
			headers,
			{ noCache: true }
		);
	},
	updateDocumentStatus({
		payload,
		headers = {},
	}: GroupedDocumentsMutationArgs) {
		return Repository.putWithAuth(
			`${resource}/documents/grouped/status`,
			payload,
			headers,
			{ noCache: true }
		);
	},
	async getAutomationDataImagesStreaming({
		groupedDocumentId,
		images,
	}: GetAutomationDataImagesStreamingArgs) {
		// Copy imagesArray to avoid mutating the original array
		const imagesArrayCopy = JSON.parse(JSON.stringify(images));

		const db = await openDB();
		const downloadPromises = [];

		imagesArrayCopy.forEach((image) => {
			image.base64Images.forEach((field) => {
				const downloadPromise = (async () => {
					try {
						const cachedImage = await getImageFromCache(db, field.base64File);

						if (cachedImage) {
							field.base64File = cachedImage;
							return cachedImage;
						}

						const response = await Repository.postWithAuth(
							`${resource}/documents/grouped/image/${groupedDocumentId}`,
							{ key: field.base64File },
							{},
							{ responseType: 'blob' }
						);

						const blob = response.data;

						if (!(blob instanceof Blob)) {
							throw new Error('Response data is not a Blob');
						}

						const base64Data = await new Promise<string>((resolve, reject) => {
							const reader = new FileReader();
							reader.onloadend = () => {
								// readAsDataURL always yields a string result.
								const result = reader.result as string;
								const base64String = result.split(',')[1] || result;
								resolve(base64String);
							};
							reader.onerror = reject;
							reader.readAsDataURL(blob);
						});

						await cacheImage(db, field.base64File, base64Data);

						field.base64File = base64Data;

						return base64Data;
					} catch (error) {
						console.error('Error processing image response:', error);
						throw error;
					}
				})();

				downloadPromises.push(downloadPromise);
			});
		});

		await Promise.all(downloadPromises);

		return imagesArrayCopy;
	},
	getAutomationDataImages({ groupedDocumentId }: GetAutomationDataImagesArgs) {
		return Repository.getWithAuth(
			`${resource}/documents/grouped/automated/${groupedDocumentId}`
		);
	},
	getAllStatuses() {
		return Repository.getWithAuthAndCache(
			`${resource}/documents/grouped/statuses`,
			fullDayInSeconds
		);
	},
	postMessage({
		payload,
		caseId,
		entityId,
		headers = {},
	}: {
		payload: CasePayload;
		caseId: string;
		entityId: string;
		headers?: CaseHeaders;
	}) {
		return Repository.postWithAuth(
			`${resource}/cases/${caseId}/${entityId}/message`,
			payload,
			headers
		);
	},
	terminateSession({ sessionId }: SessionIdArgs) {
		return Repository.postWithAuth(
			`${resource}/cases/terminate/${sessionId}`,
			{},
			// The analyzer audit trail drops any request without this header.
			{ 'x-action-name': ACTION_NAMES.TERMINATE_CFM_SESSION },
			{ noCache: true }
		);
	},
	getJourneys({ caseId }: CaseIdArgs) {
		return Repository.getWithAuth(`${resource}/cases/${caseId}/journeys`);
	},
	getMessages({ caseId }: CaseIdArgs) {
		return Repository.getWithAuth(`${resource}/cases/${caseId}/messages`);
	},
	updateEntityStatus({
		caseId,
		payload,
		headers = {},
	}: {
		caseId: string;
		payload: CasePayload;
		headers?: CaseHeaders;
	}) {
		return Repository.postWithAuth(
			`${resource}/entities/status/${caseId}`,
			payload,
			headers,
			{ noCache: true }
		);
	},
	getCaseConstants({
		merchantId,
		caseId,
		payload,
		headers = {},
	}: GetCaseConstantsArgs) {
		const normalizedPayload = stripMerchantFields(payload);
		const target = merchantId
			? caseId
				? `${resource}/constants/${merchantId}/${caseId}/find`
				: `${resource}/constants/${merchantId}/find`
			: `${resource}/constants/find`;

		return Repository.postWithAuth(target, normalizedPayload, headers);
	},
	getCaseFiles({
		caseId,
		docId,
		config = {},
	}: {
		caseId: string;
		docId: string;
		config?: Record<string, unknown>;
	}) {
		return Repository.getWithAuth(
			`${resource}/documents/${caseId}/${docId}`,
			{},
			undefined,
			{
				...config,
				responseType: 'blob',
			}
		);
	},

	getContinueSessionLink({ sessionId }: SessionIdArgs) {
		return Repository.getWithAuth(`${resource}/cases/continue/${sessionId}`);
	},
	downloadCSVCases({ payload }: DownloadCSVCasesArgs) {
		return Repository.postWithAuth(
			`${resource}/generate/csv`,
			payload,
			{},
			{ responseType: 'blob' }
		);
	},
	triggerCaseExport({
		payload,
		headers = {},
	}: {
		payload: { caseIds: string[] };
		headers?: CaseHeaders;
	}) {
		return Repository.postWithAuth(
			`${resource}/cases/export`,
			payload,
			headers
		);
	},
	getExports(payload = {}, headers = {}) {
		return Repository.postWithAuth(`${resource}/exports`, payload, headers);
	},
	getExportDetails(id: string, headers = {}) {
		return Repository.getWithAuth(`${resource}/exports/${id}`, headers);
	},
};
